import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, ClipboardCheck, UserCheck, UserX, Wallet, Home as HomeIcon,
  CalendarDays, Receipt, Banknote, Calculator, PhoneCall, UserCog,
  BarChart3, ScrollText, Settings as SettingsIcon, Bell, LogOut, Menu,
  Stethoscope, HandCoins, Fuel, CircleUser, X, ShieldCheck, Star,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDateTime, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const NAV = {
  admin: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, primary: true },
    { to: "/patients", label: "Patients", icon: Users, primary: true },
    { to: "/clinic", label: "Today's Clinic", icon: ClipboardCheck, primary: true },
    { to: "/present", label: "Present Patients", icon: UserCheck },
    { to: "/absent", label: "Absent Patients", icon: UserX },
    { to: "/payments", label: "Payments", icon: Wallet },
    { to: "/home-dashboard", label: "Home Dashboard", icon: LayoutDashboard, primary: true },
    { to: "/home-visits", label: "Home Visits", icon: HomeIcon, primary: true },
    { to: "/appointments", label: "Appointments", icon: CalendarDays },
    { to: "/deposits", label: "Deposits", icon: Banknote },
    { to: "/expenses", label: "Expenses", icon: Fuel },
    { to: "/settlements", label: "Settlements", icon: Calculator },
    { to: "/follow-ups", label: "Follow-ups", icon: PhoneCall },
    { to: "/reviews", label: "Google Reviews", icon: Star },
    { to: "/staff", label: "Staff", icon: UserCog },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/data-quality", label: "Data Quality", icon: ShieldCheck },
    { to: "/audit", label: "Audit Logs", icon: ScrollText },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
  ],
  receptionist: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, primary: true },
    { to: "/patients", label: "Patients", icon: Users, primary: true },
    { to: "/clinic", label: "Today's Clinic", icon: ClipboardCheck, primary: true },
    { to: "/present", label: "Present Patients", icon: UserCheck },
    { to: "/absent", label: "Absent Patients", icon: UserX },
    { to: "/payments", label: "Payments", icon: Wallet, primary: true },
    { to: "/follow-ups", label: "Follow-ups", icon: PhoneCall },
    { to: "/reviews", label: "Google Reviews", icon: Star },
    { to: "/appointments", label: "Appointments", icon: CalendarDays },
  ],
  physiotherapist: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, primary: true },
    { to: "/home-dashboard", label: "Home Dashboard", icon: LayoutDashboard, primary: true },
    { to: "/home-visits", label: "My Visits", icon: HomeIcon, primary: true },
    { to: "/patients", label: "My Patients", icon: Users, primary: true },
    { to: "/collections", label: "Collections", icon: HandCoins, primary: true },
    { to: "/expenses", label: "Expenses", icon: Fuel },
    { to: "/settlement", label: "Settlement", icon: Calculator },
    { to: "/profile", label: "Profile", icon: CircleUser },
  ],
};

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch (e) {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-full hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5 text-slate-600" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-bk-danger text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">Notifications</span>
          <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-bk-blue hover:underline">Mark all read</button>
        </div>
        <div className="max-h-96 overflow-auto">
          {data.items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No notifications</div>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 ${!n.read ? "bg-bk-lightGreen/40" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-bk-green shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{n.title}</p>
                  <p className="text-xs text-slate-500 line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{fmtDateTime(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children }) {
  const { user, logout, settings } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const nav = NAV[user?.role] || [];
  const primary = nav.filter((n) => n.primary).slice(0, 4);
  const roleLabel = { admin: "Admin / Owner", receptionist: "Receptionist", physiotherapist: "Home Physiotherapist" }[user?.role];

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const NavList = ({ onClick }) => (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          onClick={onClick}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-bk-green text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px]" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-white z-30">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b">
          <img src="/logo.png" alt="BK" className="h-9 w-9 rounded-lg" />
          <div className="leading-tight">
            <p className="font-display font-extrabold text-[15px] text-bk-blue">BK Spine Care</p>
            <p className="text-[10px] text-slate-400 -mt-0.5">Physiotherapy Clinic</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-4 no-scrollbar">
          <NavList />
        </div>
        <div className="border-t p-3">
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-bk-danger transition-colors">
            <LogOut className="h-[18px] w-[18px]" /> Logout
          </button>
        </div>
      </aside>

      {/* Header */}
      <header className="fixed top-0 right-0 left-0 lg:left-64 h-16 bg-white/90 backdrop-blur border-b border-border z-20 flex items-center justify-between px-4 lg:px-6">
        <div className="flex items-center gap-2 lg:hidden">
          <img src="/logo.png" alt="BK" className="h-8 w-8 rounded-md" />
          <span className="font-display font-bold text-bk-blue">BK Spine Care</span>
        </div>
        <div className="hidden lg:block">
          <p className="text-sm text-slate-500">{settings?.clinic_name || "BK Spine Care Physiotherapy Clinic"} <span className="text-slate-300">·</span> {settings?.city || "Ranchi"}</p>
        </div>
        <div className="flex items-center gap-1 lg:gap-3">
          <NotificationBell />
          <div className="hidden sm:flex items-center gap-2 pl-2 border-l">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-bk-lightGreen text-bk-green text-xs font-semibold">{initials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-slate-700 max-w-[140px] truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-400">{roleLabel}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="lg:pl-64 pt-16 pb-24 lg:pb-8">
        <div className="p-4 lg:p-6 max-w-[1400px] mx-auto bk-fade-up">{children}</div>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-border z-30 flex items-stretch">
        {primary.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`bottomnav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium ${isActive ? "text-bk-green" : "text-slate-500"}`
            }
          >
            <n.icon className="h-5 w-5" />
            {n.label.split(" ")[0]}
          </NavLink>
        ))}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button data-testid="bottomnav-more" className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-slate-500">
              <Menu className="h-5 w-5" /> More
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 p-0 flex flex-col h-full max-h-screen bg-white">
            <div className="flex items-center justify-between h-16 px-5 border-b shrink-0 bg-white">
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="BK" className="h-8 w-8 rounded-md" />
                <div className="leading-tight">
                  <p className="font-display font-bold text-bk-blue text-sm">BK Spine Care</p>
                  <p className="text-[10px] text-slate-400">{roleLabel}</p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto py-3 pb-24 touch-pan-y overscroll-contain">
              <NavList onClick={() => setMoreOpen(false)} />
              <div className="border-t mt-3 pt-3 px-3">
                <button onClick={logout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-bk-danger hover:bg-red-50">
                  <LogOut className="h-[18px] w-[18px]" /> Logout
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
